import { isEqual } from 'underscore';
import {
    Bounds, Line, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import ConnectorItem from './connectorItem';
import ContainerItem from './containerItem';
import CutangleItem from './cutangleItem';
import CutsquareItem from './cutsquareItem';
import DrillItem from './drillItem';
import TablelegItem from './tablelegItem';
import TabletopGroupItem from './tabletopGroupItem';
import VentilationgrillItem from './ventilationgrillItem';

namespace('Ofcs.Editor.Item');

/**
 * @class TabletopItem
 * @extends ContainerItem
 */
export default window.Ofcs.Editor.Item.TabletopItem = ContainerItem.extend({

    type: 'tabletop',

    cutIndex: {},

    originalPath: null,

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {FoamItem} item Me
     */

    defaults: {
        /**
         * @property {string} Type
         */
        type: null,

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the foam
         * @property {number} attributes.width
         */
        width: 0,

        /**
         * Height of the foam
         * @property {number} attributes.height
         */
        height: 0,

        /**
         * Extension of the foam
         * @property {number} attributes.extension
         */
        extension: 0,

        /**
         * Path to layout image
         * @property {string} attributes.layoutImage
         */
        layoutImage: '/bundles/ofcseditor/img/tabletop-pattern-1g63a7.jpg',

        /**
         * Path to background image
         * @property {string} attributes.backgroundImage
         */
        backgroundImage: '',

        /**
         * Color for the layout background
         * @cfg {string} [attributes.layoutColor=#2d3539]
         */
        layoutColor: '#2d3539',

        /**
         * Color for the bags background
         * @cfg {string} [attributes.bagColor=#0074c9]
         */
        bagColor: '#404B51',

        /**
         * Note for this layout
         * @cfg {string} attributes.note
         */
        note: '',

        /**
         * Array with tabletop outline segments
         * @property {Line[]} attributes.outlines
         */
        outlines: [],

        /**
         * Array with tabletop outline vertices
         * @property {Point[]} attributes.outlinePath
         */
        outlinePath: [],

        /**
         * Sealing status
         * @property {boolean} attributes.hasSealing
         */
        hasSealing: false,

        /**
         * The width of the fixed corner cut if this table is a corner tabletop
         * @property {number}
         */
        cornerCutWidth: 0,
    },

    /**
     * @constructor
     */
    initialize() {
        ContainerItem.prototype.initialize.call(this);
        this.supportsCollisions = true;
        this.supportsPositioning = true;

        this.initOutlines();

        this.set('ext', this.get('orig'));
        this.set('safety', [this.get('orig')[0], this.calculateSegments(1)]);
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getBounds() {
        return this.cache.get('bounds', () => new Bounds(
            new Point(this.get('position').x, this.get('position').y),
            new Point(this.get('position').x + this.get('width'), this.get('position').y + this.get('height')),
        ));
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        return this.cache.get('marginBounds', () => new Bounds(
            this.get('position').clone(),
            new Point(this.get('position').x + this.get('width'), this.get('position').y + this.get('height')),
        ));
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getInnerBounds() {
        return this.cache.get('innerBounds', () => {
            const safetyMargin = this.getSafetyMargin() || 0;
            return new Bounds(
                new Point(this.get('position').x + safetyMargin, this.get('position').y + safetyMargin),
                new Point(
                    (this.get('position').x + this.get('width')) - safetyMargin,
                    (this.get('position').y + this.get('height')) - safetyMargin,
                ),
            );
        });
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return null;
    },

    calculateSegments(extension = 0) {
        const top = 0 - extension;
        const left = 0 - extension;
        const bottom = this.get('height') + extension;
        const right = this.get('width') + extension;

        return [
            ['M', left, top],
            ['L', right, top],
            ['L', right, bottom],
            ['L', left, bottom],
            ['L', left, top],
        ];
    },

    /**
     * Get the area of the tabletop
     *
     * @return {number}
     */
    getArea() {
        // TODO: Remove this ugly shit!
        return Ofcs.Editor.Svg.Handle.CutangleHelper.prototype.getPolygonArea(this.getOutlinePath());
    },

    /**
     * Saves outline segments upon tabletop creation
     */
    initOutlines() {
        // todo: adjust to possible polygon shapes
        const top = new Line(0, 0, this.get('width'), 0);
        const right = new Line(this.get('width'), 0, this.get('width'), this.get('height'));
        const bottom = new Line(this.get('width'), this.get('height'), 0, this.get('height'));
        const left = new Line(0, this.get('height'), 0, 0);

        this.setOutlineSegments([top, right, bottom, left]);
        this.setOutlinePath([
            new Point(0, 0),
            new Point(this.get('width'), 0),
            new Point(this.get('width'), this.get('height')),
            new Point(0, this.get('height')),
            new Point(0, 0),
        ]);

        this.setOriginalPath();
    },

    /**
     * Returns all segments which currently make
     * up the tabletop's outline path
     *
     * @return {Line[]}
     */
    getOutlineSegments() {
        return this.get('outlines');
    },

    /**
     * Sets new outline segments array
     *
     * @param {Line[]} outlines
     */
    setOutlineSegments(outlines) {
        this.set('outlines', outlines);
    },

    /**
     * Returns all vertices which currently make
     * up the tabletop's outline path
     *
     * @return {Point[]}
     */
    getOutlinePath() {
        return this.get('outlinePath');
    },

    /**
     * Sets new outline path array
     *
     * @param {Point[]} path
     */
    setOutlinePath(path) {
        this.set('outlinePath', path);
    },

    setOriginalPath() {
        this.originalPath = [];

        this.getOutlinePath().forEach(item => this.originalPath.push(item.clone()));
    },

    /**
     * @return {number}
     */
    getCornerCutWidth() {
        return this.get('cornerCutWidth');
    },

    /**
     * updates cut Index
     *
     * @param {string} action - 'add' or 'remove'
     * @param {number} oldLength
     * @param {number} newLength
     * @param {Line[]} cutOutlinePath
     * @param {number} newIndex
     * @param {Object} obj
     */
    updateCutIndex(action, oldLength, newLength, cutOutlinePath, newIndex, obj) {
        const tempIndex = {};
        let streak = 0;

        if (action === 'add') {
            tempIndex[newIndex] = obj;
        }

        let diff = newLength - oldLength;

        for (let i = 0, j = 0; i < cutOutlinePath.length; i += 1) {
            if (isEqual(this.originalPath[j], cutOutlinePath[i])) {
                streak += 1;
                j += 1;
            }
        }

        if (streak - 1 > 0 && action === 'add') {
            diff -= streak - 1;
        }
        if (streak - 1 > 0 && action === 'remove') {
            diff += streak - 1;
        }

        Object
            .keys(this.cutIndex)
            .forEach((key) => {
                if (Number(key) >= newIndex || (streak - 1 > 0 && (action === 'remove' || action === 'add'))) {
                    tempIndex[Number(key) + diff] = this.cutIndex[key];
                    delete this.cutIndex[key];
                }
            });

        this.cutIndex = { ...this.cutIndex, ...tempIndex };
    },

    /**
     * returns sorted information for given cut item
     *
     * @param {CutangleItem} item
     * @return {Object}
     */
    getSorted(item) {
        const cutIndex = Object
            .values(this.cutIndex)
            .find(idx => idx.item === item);

        if (cutIndex) return cutIndex.sorted;

        return null;
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = ContainerItem.prototype.toJSON.apply(this, args);
        const path = [];
        for (let i = 0; i < attributes.outlinePath.length; i += 1) {
            path.push(attributes.outlinePath[i].toJSON());
        }

        return {
            x: attributes.x,
            y: attributes.y,
            width: attributes.width,
            height: attributes.height,
            type: attributes.type,
            version: attributes.toolVersion,
            outlinePath: path,
        };
    },

    /**
     * @param {AbstractItem} item
     * @return {boolean}
     */
    isItemSupported(item) {
        return item instanceof ConnectorItem
            || item instanceof CutangleItem
            || item instanceof CutsquareItem
            || item instanceof DrillItem
            || item instanceof TablelegItem
            || item instanceof TabletopGroupItem
            || item instanceof VentilationgrillItem;
    },
});
